/* eslint-disable @typescript-eslint/no-unused-vars */
import { RootState } from "redux/store";
import { Dip, Trigger } from "interfaces";
import { initialState } from "./initial-state";
import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { request } from "http";
import {
	abortDIPExecution,
	getDIPByTrigger,
	getDIPByTriggerAndPricePoint,
	getDIPtype,
	PREFIX
} from "./helper";
import {
	CLAIM_REWARD_EXTRA_REDUCER,
	DEBUG_EXTRA_REDUCER,
	DIP_EXTRA_REDUCER
} from "./extra-reducers";

type DIPTriggerPayload = {
	triggerType: Trigger;
	appDipId?: string;
	requestId?: string;
};

type OpenHudDipPayload = {
	dip: Dip;
	timerExpirationDate?: string;
};
export const dipSlice = createSlice({
	name: PREFIX,
	initialState,
	reducers: {
		onDIPtrigger: (state, action: PayloadAction<DIPTriggerPayload>) => {
			// prettier-ignore
			const { data, mode, debug, opened, queueTriggers, triggerCached, pricePoint } = current(state);

			// Setting the current trigger by checking first if we have a trigger
			// on chache. If that's the case we take that trigger as the new one that
			// will be executed. If It's not, then we take the trigger received
			let trigger: Trigger;
			let pricePointTrigger: number | undefined;
			if (action.payload.triggerType === "cache-mode" && triggerCached) {
				trigger = triggerCached;
				pricePointTrigger = pricePoint;
				state.triggerCached = undefined;
				state.pricePoint = undefined;
			} else {
				trigger = action.payload.triggerType;
			}

			const isDebugMode = mode === "debug";
			const list: Dip[] | undefined = isDebugMode
				? debug["data"]?.dips
				: data.dips;
			const dipFound = pricePointTrigger
				? getDIPByTriggerAndPricePoint(list ?? [], trigger, pricePointTrigger)
				: getDIPByTrigger(list ?? [], trigger);

			if (!dipFound) {
				return;
			}

			// There are some conditions where we need to abort the execution
			// take a look to the abortDIPExecution helper
			// ONE OF THE CONDITIONS COULD BE SUPRESS ON LANDING PAGE
			if (abortDIPExecution(dipFound, trigger, mode)) {
				if (
					trigger.startsWith("purchaseCancelled-") ||
					trigger !== "purchaseCancelled"
				) {
					state.triggerCached = trigger;
				}
				return;
			}

			// If there is a DIP opened and a new one is triggered, we save that new one in a queue
			// to display it after the current one is closed. See on components/Dip/hooks/useInitialActions
			if (opened) {
				if (!queueTriggers.includes(trigger)) {
					if (
						trigger.startsWith("purchaseCancelled-") ||
						trigger === "purchaseCancelled"
					) {
						return;
					}

					state.queueTriggers = [...queueTriggers, trigger];
				}
				return;
			}

			state.currentDip = {
				...dipFound,
				appDipId: action.payload.appDipId,
				requestId: action.payload.requestId
			};

			state.activeTrigger = trigger;
			state.dipType = getDIPtype(dipFound);
			state.queueTriggers = queueTriggers.filter((trig) => trig !== trigger);
			state.fromHud = false;
			state.hudTimerExpirationDate = undefined;
			state.opened = true;
		},
		onOpenHudDip: (state, action: PayloadAction<OpenHudDipPayload>) => {
			if (state.opened) {
				return;
			}

			state.currentDip = action.payload.dip;
			state.activeTrigger = undefined;
			state.dipType = getDIPtype(action.payload.dip);
			state.fromHud = true;
			state.hudTimerExpirationDate = action.payload.timerExpirationDate;
			state.opened = true;
		},
		setTriggerOnCache: (state, action: PayloadAction<Trigger>) => {
			// might need to remove this
			if (state.triggerCached === "purchaseSuccess") {
				return;
			}
			state.triggerCached = action.payload;
		},
		setPricePoint: (state, action: PayloadAction<number>) => {
			state.pricePoint = action.payload;
		},
		onCloseDIP: (state): void => {
			state.currentDip = undefined;
			state.activeTrigger = undefined;
			state.opened = false;
			state.fromHud = false;
			state.hudTimerExpirationDate = undefined;
			state.dipType = undefined;
		},
		onResolveSessionStartDIP: (state): void => {
			state.sessionStartDipStatus = "resolved";
		},
		onDisableButtonsDIP: (state): void => {
			state.claimRewardProcess.claiming = true;
		},
		onEnableButtonsDIP: (state): void => {
			state.claimRewardProcess.claiming = false;
		},
		onAddRequisitionCount: (state): void => {
			if (state.currentDip) {
				state.currentDip.requisitionCount++;
			}
		}
	},
	extraReducers: (builder) => {
		DIP_EXTRA_REDUCER(builder);
		CLAIM_REWARD_EXTRA_REDUCER(builder);
		DEBUG_EXTRA_REDUCER(builder);
	}
});

export const {
	onCloseDIP,
	onResolveSessionStartDIP,
	onDIPtrigger,
	onOpenHudDip,
	setTriggerOnCache,
	setPricePoint,
	onDisableButtonsDIP,
	onEnableButtonsDIP,
	onAddRequisitionCount
} = dipSlice.actions;

export const selectDipState = (state: RootState) => state.dip;
export default dipSlice.reducer;
export * from "./types";
export * from "./thunks";
